import { isAdministrator, type Session } from '@/lib/types';

/**
 * 오픈 게이트의 <b>순수 판정</b> — "이 사람에게 지금 닫혀 있는 메뉴는 무엇인가"
 * (백엔드 `MenuGate` · V153 · 사용자 지시 2026-08-28).
 *
 * 훅이 아니라 함수인 이유: 라우트 가드(`lib/auth/access.ts`)가 이 판정을 쓰는데, 그 파일은
 * 리액트·라우터·쿼리를 모르는 순수 모듈이다(머리말 참고). 훅을 부르게 두면 그 규율이 깨지고
 * 테스트도 렌더러를 띄워야 한다.
 *
 * <h2>관리자는 게이트 밖이다</h2>
 * 닫아 둔 화면을 만들고 시험하는 사람이 곧 관리자이고, 되돌리는 화면(환경설정 &gt; 메뉴 관리)까지
 * 함께 잠기면 오픈을 취소할 문이 없어진다. 백엔드도 같은 자리에서 같은 예외를 둔다.
 *
 * <h2>감추는 일일 뿐이다</h2>
 * 실제 차단은 서버가 매 요청 다시 한다. 이 함수를 지워도 닫아 둔 API 가 열려서는 안 된다.
 */
export function closedKeysFor(session: Session | undefined): Set<string> {
  if (!session?.authenticated) return new Set();
  if (isAdministrator(session.user?.userType)) return new Set();
  return new Set(session.closedMenuKeys ?? []);
}

/**
 * 홈 위젯의 게이트 키 — 위젯 키 앞에 {@code widget_} 을 붙인다.
 *
 * <h2>왜 접두어인가</h2>
 * 위젯 키와 메뉴 키가 <b>같은 이름을 쓴다</b>(`notices`·`news`·`trends`·`site_summary`…). 한 통에
 * 섞으면 「공지사항 메뉴」를 닫았을 뿐인데 「공지사항 위젯」까지 닫히거나 그 반대가 된다 — 게다가
 * 어느 쪽을 끈 것인지 표에서 구분할 방법이 없다.
 *
 * <h2>메뉴를 닫으면 위젯도 닫힌다 — 이것과 별개로</h2>
 * 위젯 카탈로그는 이미 {@code requiresMenuKey} 로 걸러진다(`useHomeWidgets`). 그래서 자료실을
 * 닫으면 자료실 위젯은 저절로 빠진다. 이 키는 <b>메뉴는 열어 두고 위젯만</b> 내리는 자리다 —
 * 「입·퇴사자 소식은 게시판으로는 보되 홈 첫 화면에는 띄우지 않는다」 같은 요구가 그것이다.
 */
export function widgetGateKey(widgetKey: string): string {
  return `widget_${widgetKey}`;
}
